import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap';
import reactStringReplace from 'react-string-replace';
import axios from 'axios';
import * as Icons from 'react-feather';
import AddProduct from './AddProduct';
import EditProduct from './EditProduct';
import ViewProduct from './ViewProduct';
import DeleteProduct from './DeleteProduct';
import ProductModel from './models/ProductModel';
import { logAuditEvent } from '../../../utils/auditLogger';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getStorageValues } from '../../../shared/CommonFunctions';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { buildDetailTabsSavePayload, detailTabsNeedSave, normalizeDetailTabsFromApi } from './utils/productDetailTabKeys';
import { getActiveDetailTabTypes } from './utils/mapProduct';

function ProductModal({
  onClose,
  onSubmit,
  UserAction,
  show,
  selectedRow,
  moduleName = 'Product',
  modalSize = 'xl',
}) {
  const [childData, setChildData] = useState(new ProductModel());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingFeatureOrder, setPendingFeatureOrder] = useState(null);
  const [featureOrderSaveToken, setFeatureOrderSaveToken] = useState(0);
  const [pendingSpecificationOrder, setPendingSpecificationOrder] = useState(null);
  const [specOrderSaveToken, setSpecOrderSaveToken] = useState(0);
  const [pendingDetailTabs, setPendingDetailTabs] = useState(null);
  const [detailTabsSaveToken, setDetailTabsSaveToken] = useState(0);
  const [activeTabTypes, setActiveTabTypes] = useState([]);
  const [alert, setAlert] = useState(null);
  const userAction = reactStringReplace(UserAction, 'User', () => '');

  useEffect(() => {
    if (show && selectedRow) {
      setChildData({ ...selectedRow });
    } else if (!show) {
      setChildData(new ProductModel());
      setPendingFeatureOrder(null);
      setPendingSpecificationOrder(null);
      setPendingDetailTabs(null);
    }
  }, [selectedRow, show]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    getActiveDetailTabTypes(true).then((types) => {
      if (!cancelled) setActiveTabTypes(types);
    });
    return () => {
      cancelled = true;
    };
  }, [show]);

  const handleDataFromChild = (data) => setChildData(data);

  const persistFeatureOrder = async () => {
    if (!pendingFeatureOrder?.dirty || !pendingFeatureOrder.orderList?.length) {
      return true;
    }
    try {
      await axios.post(`${getApiBaseUrl()}/ec-product/feature/update-order`, {
        ProductId: pendingFeatureOrder.productId,
        OrderList: pendingFeatureOrder.orderList,
        UserId: getStorageValues('userId'),
      });
      setPendingFeatureOrder(null);
      setFeatureOrderSaveToken((t) => t + 1);
      return true;
    } catch (err) {
      console.error(err);
      CreateAlert('Unable to save feature order', 'Error', setAlert);
      return false;
    }
  };

  const persistSpecificationOrder = async () => {
    if (!pendingSpecificationOrder?.dirty || !pendingSpecificationOrder.orderList?.length) {
      return true;
    }
    try {
      await axios.post(`${getApiBaseUrl()}/ec-product/specification/update-order`, {
        ProductId: pendingSpecificationOrder.productId,
        OrderList: pendingSpecificationOrder.orderList,
        UserId: getStorageValues('userId'),
      });
      setPendingSpecificationOrder(null);
      setSpecOrderSaveToken((t) => t + 1);
      return true;
    } catch (err) {
      console.error(err);
      CreateAlert('Unable to save specification order', 'Error', setAlert);
      return false;
    }
  };

  const persistDetailTabs = async (productId) => {
    const tabsToSave = pendingDetailTabs?.tabs
      ?? normalizeDetailTabsFromApi(childData?.DetailTabs, activeTabTypes);
    const baselineTabs = selectedRow?.DetailTabs ?? [];
    const needsSave = detailTabsNeedSave(tabsToSave, baselineTabs, activeTabTypes);

    if (!productId || !Array.isArray(tabsToSave) || tabsToSave.length === 0 || !needsSave) {
      return true;
    }

    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product/detail-tabs/save`, {
        ProductId: productId,
        Tabs: buildDetailTabsSavePayload(tabsToSave, activeTabTypes),
        UserId: getStorageValues('userId'),
      });
      const result = response.data?.data;
      if (result?.success !== 1) {
        CreateAlert(result?.message || 'Unable to save detail page tabs', 'Error', setAlert);
        return false;
      }
      setPendingDetailTabs(null);
      setDetailTabsSaveToken((t) => t + 1);
      return true;
    } catch (err) {
      console.error(err);
      CreateAlert('Unable to save detail page tabs', 'Error', setAlert);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const actionDescriptor = UserAction === 'Delete' ? 'Delete Confirm' : 'Save';
    logAuditEvent(moduleName, `${UserAction || 'Action'} ${actionDescriptor}`, `${actionDescriptor} clicked`);
    try {
      const featureOrderSaved = await persistFeatureOrder();
      if (!featureOrderSaved) return;
      const specOrderSaved = await persistSpecificationOrder();
      if (!specOrderSaved) return;
      const submitResult = await onSubmit(e, childData);
      if (!submitResult?.success) return;
      const detailTabsSaved = await persistDetailTabs(submitResult.productId);
      if (!detailTabsSaved) return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (label) => {
    logAuditEvent(moduleName, `${UserAction || 'Action'} ${label}`, `${label} clicked`);
    setPendingFeatureOrder(null);
    setPendingSpecificationOrder(null);
    setPendingDetailTabs(null);
    onClose();
  };

  const handleLoad = (data) => setChildData(data);

  let modalAction = null;
  let showButtons = null;

  if (UserAction === 'Add') {
    modalAction = <AddProduct onLoad={handleLoad} sendDataToParent={handleDataFromChild} />;
    showButtons = (
      <div>
        <button className="btn btn-cancel me-2" name="Add" onClick={() => handleCancel('Cancel')} disabled={isSubmitting}>Cancel</button>
        <button className="btn btn-submit" name="Add" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    );
  } else if (UserAction === 'Edit') {
    modalAction = (
      <EditProduct
        ProductData={childData}
        sendDataToParent={handleDataFromChild}
        onFeatureOrderChange={setPendingFeatureOrder}
        featureOrderSaveToken={featureOrderSaveToken}
        onSpecificationOrderChange={setPendingSpecificationOrder}
        specOrderSaveToken={specOrderSaveToken}
        onDetailTabsChange={setPendingDetailTabs}
        detailTabsSaveToken={detailTabsSaveToken}
        tabTypes={activeTabTypes}
      />
    );
    showButtons = (
      <div>
        <button className="btn btn-cancel me-2" name="Edit" onClick={() => handleCancel('Cancel')} disabled={isSubmitting}>Cancel</button>
        <button className="btn btn-submit" name="Edit" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    );
  } else if (UserAction === 'View') {
    modalAction = <ViewProduct ProductData={selectedRow} tabTypes={activeTabTypes} />;
    showButtons = (
      <button className="btn btn-cancel me-2" name="View" onClick={() => handleCancel('Close')}>Close</button>
    );
  } else if (UserAction === 'Delete') {
    modalAction = <DeleteProduct ProductData={selectedRow} />;
    showButtons = (
      <div>
        <button className="btn btn-sm btn-secondary m-1" name="Delete" onClick={() => handleCancel('Delete Cancel')}>Cancel</button>
        <button className="btn btn-sm btn-danger m-1" name="Delete" onClick={handleSubmit} disabled={isSubmitting}>Delete</button>
      </div>
    );
  }

  if (UserAction === 'Delete') {
    return (
      <Modal show={show} onHide={() => handleCancel('Close')} backdrop="static">
        <ModalHeader className="alert custom-alert1 alert-danger modalheader-width">
          <ModalTitle>
            <div className="custom-alert-icon"><Icons.AlertOctagon className="flex-shrink-0" /></div>
            <div>{userAction} Product</div>
          </ModalTitle>
        </ModalHeader>
        <ModalBody>{modalAction}</ModalBody>
        <ModalFooter className="modalfooter-width">{showButtons}</ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={() => handleCancel('Close')} backdrop="static" size={modalSize}>
      <ModalHeader className="modal-header border-0 custom-modal-header" closeButton>
        <ModalTitle>{userAction} Product</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <SKAlerts alert={alert} />
        {modalAction}
      </ModalBody>
      <ModalFooter>{showButtons}</ModalFooter>
    </Modal>
  );
}

export default ProductModal;
